import { Component, OnInit } from '@angular/core';
import {
  EditorialArticle,
  EditorialClaim,
  EditorialStatus,
} from 'src/app/models/editorial/editorialArticle';
import {
  EditorialSource,
  ImageLicenceStatus,
} from 'src/app/models/editorial/editorialSource';
import { ResearchJob } from 'src/app/models/editorial/researchJob';
import { EditorialService } from 'src/app/services/editorial-service';

@Component({
  selector: 'pla-admin-editorial-review',
  templateUrl: './admin-editorial-review.component.html',
  styleUrls: ['./admin-editorial-review.component.scss'],
})
export class AdminEditorialReviewComponent implements OnInit {
  public EditorialStatus = EditorialStatus;
  public ImageLicenceStatus = ImageLicenceStatus;

  public statusFilter: EditorialStatus | 'all' = EditorialStatus.DRAFT;
  public list: EditorialArticle[] = [];
  public selected: EditorialArticle = null;
  public jobs: ResearchJob[] = [];
  public loading = false;
  public busy = false;

  // Seed / batch form state.
  public seedReg = '';
  public batchCount = 100;
  public batchMinPrice: number = null;
  public batchSinceDays = 90;
  public batchStyle = '';

  constructor(private editorialService: EditorialService) {}

  ngOnInit(): void {
    this.refresh();
    this.refreshJobs();
  }

  public refresh(): void {
    this.loading = true;
    this.editorialService.listForReview(this.statusFilter).subscribe(
      (res) => {
        this.list = res || [];
        this.loading = false;
      },
      () => {
        this.list = [];
        this.loading = false;
      }
    );
  }

  public refreshJobs(): void {
    this.editorialService.listJobs().subscribe(
      (res) => (this.jobs = res || []),
      () => (this.jobs = [])
    );
  }

  public open(article: EditorialArticle): void {
    this.editorialService.getArticle(article.id).subscribe((full) => {
      this.selected = full;
    });
  }

  public close(): void {
    this.selected = null;
  }

  // Resolve a claim's backing sources for display.
  public sourcesFor(claim: EditorialClaim): EditorialSource[] {
    if (!this.selected) return [];
    return (this.selected.sources || []).filter((s) =>
      claim.sourceIds.includes(s.id)
    );
  }

  public confidenceClass(confidence: number): string {
    if (confidence >= 0.75) return 'conf-high';
    if (confidence >= 0.45) return 'conf-med';
    return 'conf-low';
  }

  public confidenceLabel(confidence: number): string {
    return `${Math.round((confidence || 0) * 100)}%`;
  }

  // A claim with no backing sources must never be publishable.
  public hasUnsourcedClaims(): boolean {
    if (!this.selected) return false;
    return (this.selected.claims || []).some(
      (c) => !c.sourceIds || c.sourceIds.length === 0
    );
  }

  public saveEdits(): void {
    if (!this.selected) return;
    this.busy = true;
    this.editorialService
      .saveEdits(this.selected.id, {
        headline: this.selected.headline,
        summary: this.selected.summary,
        bodyMarkdown: this.selected.bodyMarkdown,
        claims: this.selected.claims,
      })
      .subscribe(
        (updated) => {
          this.selected = updated;
          this.busy = false;
        },
        () => (this.busy = false)
      );
  }

  public approve(): void {
    if (!this.selected) return;
    if (this.hasUnsourcedClaims()) {
      alert('Cannot approve: one or more claims have no backing source.');
      return;
    }
    this.busy = true;
    this.editorialService
      .approve(this.selected.id, this.selected.reviewerNotes)
      .subscribe(
        () => this.afterAction(),
        () => (this.busy = false)
      );
  }

  public reject(): void {
    if (!this.selected) return;
    const reason = prompt('Reason for rejection?');
    if (reason == null) return;
    this.busy = true;
    this.editorialService.reject(this.selected.id, reason).subscribe(
      () => this.afterAction(),
      () => (this.busy = false)
    );
  }

  public requestChanges(): void {
    if (!this.selected) return;
    const notes = prompt('What changes are needed?');
    if (notes == null) return;
    this.busy = true;
    this.editorialService.requestChanges(this.selected.id, notes).subscribe(
      () => this.afterAction(),
      () => (this.busy = false)
    );
  }

  public requestReResearch(): void {
    if (!this.selected) return;
    this.busy = true;
    this.editorialService.requestReResearch(this.selected.id).subscribe(
      () => {
        this.afterAction();
        this.refreshJobs();
      },
      () => (this.busy = false)
    );
  }

  public seed(): void {
    const reg = (this.seedReg || '').trim().toUpperCase();
    if (!reg) {
      alert('Enter a registration to research.');
      return;
    }
    this.busy = true;
    this.editorialService.seedResearch({ registration: reg, force: true }).subscribe(
      () => {
        this.seedReg = '';
        this.busy = false;
        this.refreshJobs();
      },
      () => (this.busy = false)
    );
  }

  public batch(): void {
    this.busy = true;
    this.editorialService
      .batchResearch({
        count: this.batchCount,
        minSoldPrice: this.batchMinPrice || undefined,
        soldSinceDays: this.batchSinceDays || undefined,
        style: this.batchStyle || undefined,
      })
      .subscribe(
        (res) => {
          alert(`Enqueued ${res.enqueued} research jobs.`);
          this.busy = false;
          this.refreshJobs();
        },
        () => (this.busy = false)
      );
  }

  private afterAction(): void {
    this.busy = false;
    this.selected = null;
    this.refresh();
  }
}
